import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store';
import { useAppSelector } from './store/hooks';
import { ThemeContextProvider } from "./context/ThemeContext";
import { EcptSettingsProvider } from "./ecpt/context/EcptSettingsContext";
import { AuthProvider } from "./auth/context/AuthContext";
import AuthProtectedRoute from "./auth/components/AuthProtectedRoute";
import EcptRoleProtectedRoute from "./auth/components/EcptRoleProtectedRoute";
import MainLayout from "./layouts/MainLayout";
import i18n from './i18n';
import "./i18n";

const Home2Page = lazy(() => import('./mopsc/pages/Home2Page'));
const Home = lazy(() => import('./mopsc/pages/Home'));
const EcptHomePage = lazy(() => import('./pages/ecpt/EcptHomePage'));
const DfcHomePage = lazy(() => import('./pages/dfc/DfcHomePage'));
const DigiTubeHomePage = lazy(() => import('./pages/digi-tube/DigiTubeHomePage'));
const OdinHomePage = lazy(() => import('./pages/odin/OdinHomePage'));
const EcptSettingsPage = lazy(() => import('./pages/ecpt/EcptSettingsPage'));
const EcptOperationsPage = lazy(() => import('./pages/ecpt/EcptOperationsPage'));
const EcptGetPage = lazy(() => import('./pages/ecpt/EcptGetPage'));
const EcptChatbotPage = lazy(() => import('./pages/ecpt/EcptChatbotPage'));
const ProfileSettingsPage = lazy(() => import('./pages/ProfileSettingsPage'));
const SsoLoginPage = lazy(() => import('./auth/pages/SsoLoginPage'));
const AuthCallback = lazy(() => import('./auth/pages/AuthCallbackPage'));

const PageLoader: React.FC = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100vh', backgroundColor: 'var(--clr-bg)',
  }}>
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
      stroke="var(--clr-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ animation: 'spin 1s linear infinite' }}>
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
  </div>
);

/** Keeps react-i18next in sync with the Redux language slice. */
const LanguageSync: React.FC = () => {
  const language = useAppSelector((s) => s.lang.language);
  useEffect(() => { i18n.changeLanguage(language); }, [language]);
  return null;
};

/** Layout route that adds EcptSettingsProvider — only for pages that need views data. */
const EcptViewsLayout: React.FC = () => (
  <EcptSettingsProvider>
    <Outlet />
  </EcptSettingsProvider>
);

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <LanguageSync />
        <ThemeContextProvider>
          <BrowserRouter>
            <AuthProvider>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes — no auth required */}
                  <Route path="/mopsc-app-station/login" element={<SsoLoginPage />} />
                  <Route path="/mopsc-app-station/auth/callback" element={<AuthCallback />} />

                  {/* Protected non-ECPT routes */}
                  <Route
                    path="/mopsc-app-station/home"
                    element={
                      <AuthProtectedRoute>
                        <MainLayout><Home2Page /></MainLayout>
                      </AuthProtectedRoute>
                    }
                  />
                  <Route
                    path="/mopsc-app-station-v2/home"
                    element={
                      <AuthProtectedRoute>
                        <MainLayout><Home /></MainLayout>
                      </AuthProtectedRoute>
                    }
                  />
                  <Route
                    path="/mopsc-app-station-v3/home"
                    element={
                      <AuthProtectedRoute>
                        <MainLayout><Home /></MainLayout>
                      </AuthProtectedRoute>
                    }
                  />
                  <Route path="/mopsc-app-station/profile-settings" element={<AuthProtectedRoute><MainLayout><ProfileSettingsPage /></MainLayout></AuthProtectedRoute>} />
                  <Route path="/mopsc-app-station-v2/profile-settings" element={<AuthProtectedRoute><MainLayout><ProfileSettingsPage basePath="/mopsc-app-station-v2" /></MainLayout></AuthProtectedRoute>} />
                  <Route path="/mopsc-app-station-v3/profile-settings" element={<AuthProtectedRoute><MainLayout><ProfileSettingsPage basePath="/mopsc-app-station-v3" /></MainLayout></AuthProtectedRoute>} />

                  {/* DFC home */}
                  <Route path="/mopsc-app-station/dfc/home" element={<AuthProtectedRoute><MainLayout><DfcHomePage /></MainLayout></AuthProtectedRoute>} />

                  {/* Digi Tube home */}
                  <Route path="/mopsc-app-station/digi-tube/home" element={<AuthProtectedRoute><MainLayout><DigiTubeHomePage /></MainLayout></AuthProtectedRoute>} />

                  {/* ODIN home */}
                  <Route path="/mopsc-app-station/odin/home" element={<AuthProtectedRoute><MainLayout><OdinHomePage /></MainLayout></AuthProtectedRoute>} />

                  {/* ECPT home — no views fetch needed */}
                  <Route path="/mopsc-app-station/ecpt/home" element={<AuthProtectedRoute><EcptRoleProtectedRoute><MainLayout><EcptHomePage cardVariant="v3" /></MainLayout></EcptRoleProtectedRoute></AuthProtectedRoute>} />
                  <Route path="/mopsc-app-station-v2/ecpt/home" element={<AuthProtectedRoute><EcptRoleProtectedRoute><MainLayout><EcptHomePage basePath="/mopsc-app-station-v2/ecpt" /></MainLayout></EcptRoleProtectedRoute></AuthProtectedRoute>} />
                  <Route path="/mopsc-app-station-v3/ecpt/home" element={<AuthProtectedRoute><EcptRoleProtectedRoute><MainLayout><EcptHomePage basePath="/mopsc-app-station-v3/ecpt" cardVariant="v3" /></MainLayout></EcptRoleProtectedRoute></AuthProtectedRoute>} />

                  {/* ECPT settings/operations/get — EcptSettingsProvider fetches views only here */}
                  <Route element={<AuthProtectedRoute><EcptRoleProtectedRoute><EcptViewsLayout /></EcptRoleProtectedRoute></AuthProtectedRoute>}>
                    <Route path="/mopsc-app-station/ecpt/settings" element={<MainLayout><EcptSettingsPage /></MainLayout>} />
                    <Route path="/mopsc-app-station/ecpt/operations" element={<MainLayout><EcptOperationsPage /></MainLayout>} />
                    <Route path="/mopsc-app-station/ecpt/get" element={<MainLayout><EcptGetPage /></MainLayout>} />
                    <Route path="/mopsc-app-station/ecpt/chatbot" element={<MainLayout><EcptChatbotPage /></MainLayout>} />

                    <Route path="/mopsc-app-station-v2/ecpt/settings" element={<MainLayout><EcptSettingsPage basePath="/mopsc-app-station-v2/ecpt" /></MainLayout>} />
                    <Route path="/mopsc-app-station-v2/ecpt/operations" element={<MainLayout><EcptOperationsPage basePath="/mopsc-app-station-v2/ecpt" /></MainLayout>} />
                    <Route path="/mopsc-app-station-v2/ecpt/get" element={<MainLayout><EcptGetPage basePath="/mopsc-app-station-v2/ecpt" /></MainLayout>} />
                    <Route path="/mopsc-app-station-v2/ecpt/chatbot" element={<MainLayout><EcptChatbotPage basePath="/mopsc-app-station-v2/ecpt" /></MainLayout>} />

                    <Route path="/mopsc-app-station-v3/ecpt/settings" element={<MainLayout><EcptSettingsPage basePath="/mopsc-app-station-v3/ecpt" /></MainLayout>} />
                    <Route path="/mopsc-app-station-v3/ecpt/operations" element={<MainLayout><EcptOperationsPage basePath="/mopsc-app-station-v3/ecpt" /></MainLayout>} />
                    <Route path="/mopsc-app-station-v3/ecpt/get" element={<MainLayout><EcptGetPage basePath="/mopsc-app-station-v3/ecpt" /></MainLayout>} />
                    <Route path="/mopsc-app-station-v3/ecpt/chatbot" element={<MainLayout><EcptChatbotPage basePath="/mopsc-app-station-v3/ecpt" /></MainLayout>} />
                  </Route>

                  {/* Redirects */}
                  <Route path="/mopsc-app-station/ecpt/login" element={<Navigate to="/mopsc-app-station/login" replace />} />
                  <Route path="/mopsc-app-station-v2/ecpt/login" element={<Navigate to="/mopsc-app-station/login" replace />} />
                  <Route path="/mopsc-app-station-v3/ecpt/login" element={<Navigate to="/mopsc-app-station/login" replace />} />
                  <Route path="/mopsclogin" element={<Navigate to="/mopsc-app-station/login" replace />} />
                  <Route path="/" element={<Navigate to={(process.env.PUBLIC_URL || '/mopsc-app-station') + '/home'} replace />} />
                  <Route path="*" element={<Navigate to={(process.env.PUBLIC_URL || '/mopsc-app-station') + '/home'} replace />} />
                </Routes>
              </Suspense>
            </AuthProvider>
          </BrowserRouter>
        </ThemeContextProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
